import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  emptyHardwareOrder,
  hardwareOrderTotal,
  hasHardwareOrder,
  isBlockAnswered,
  NUMBER_REQUEST_HANDSET_IMAGES,
  NUMBER_REQUEST_HANDSET_MAX_QUANTITY,
  NUMBER_REQUEST_HANDSET_UNIT_PRICE_EUR,
  NUMBER_REQUEST_HANDSETS,
  type NumberRequest,
  type NumberRequestHandset,
  type NumberRequestHardwareOrder,
  type UpdateNumberRequestData,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckCircleIcon,
  MinusIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { EditableBlock } from "./editable-block";

/**
 * What the client answers the transferred calls on (`port_existing` only).
 *
 * Forwarding their number to HeyUp is the easy half; the calls we hand back —
 * the ones the agent transfers — have to land somewhere. That is a small order
 * rather than a single pick: the counter wants a desk phone, the back room
 * wants a cordless one, and both at once is an ordinary case, so each model
 * carries its own quantity.
 *
 * Ordering nothing is a valid answer, and the common one: with no handset the
 * transfers go to the mobile number configured on the account. So this is an
 * offer, not a question blocking the file — hence an optional block, and hence
 * its place after the checklist rather than among the information: it is worth
 * thinking about once the paperwork is done, not before.
 *
 * Bringing your own VoIP phone is the third way in, but it is for the handful
 * of clients already running SIP hardware: worth exactly one line, folded away
 * behind a link so it doesn't compete with the two products.
 */
export function HardwareChoice({
  request,
  isSaving,
  onSave,
  readOnly,
}: {
  request: NumberRequest;
  isSaving: boolean;
  onSave: (data: UpdateNumberRequestData) => Promise<boolean>;
  /** The file is closed: the order is shown, nothing can be changed. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation("numberRequests");

  const [order, setOrder] = useState<NumberRequestHardwareOrder>(
    request.hardware_order || emptyHardwareOrder(),
  );

  useEffect(() => {
    setOrder(request.hardware_order || emptyHardwareOrder());
  }, [request.hardware_order]);

  const setQuantity = (handset: NumberRequestHandset, quantity: number) =>
    setOrder((current) => ({
      ...current,
      quantities: { ...current.quantities, [handset]: Math.max(0, quantity) },
    }));

  const submit = async (event: React.FormEvent, close: () => void) => {
    event.preventDefault();
    if (await onSave({ hardware_order: order, answered_block: "hardware" }))
      close();
  };

  const total = hardwareOrderTotal(order);

  return (
    <EditableBlock
      title={t("numberRequests.details.hardware")}
      description={t("numberRequests.details.hardwareHelp")}
      filled={hasHardwareOrder(request.hardware_order)}
      answered={isBlockAnswered(request, "hardware")}
      optional
      readOnly={readOnly}
      // Always a summary, including for an empty order: "nothing, so the
      // transfers ring on the mobile" is the answer, and a block that reads
      // "Non renseigné" would look like it had been skipped.
      summary={<Summary order={request.hardware_order} />}
    >
      {(close) => (
        <form onSubmit={(event) => submit(event, close)} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {NUMBER_REQUEST_HANDSETS.map((handset) => (
              <HandsetCard
                key={handset}
                handset={handset}
                quantity={order.quantities?.[handset] || 0}
                onQuantityChange={(quantity) => setQuantity(handset, quantity)}
              />
            ))}
          </div>

          <p className="text-muted-foreground text-xs">
            {t("numberRequests.details.hardwarePhotosDisclaimer")}
          </p>

          <OwnDeviceDisclosure
            checked={order.own_device}
            onChange={(own_device) =>
              setOrder((current) => ({ ...current, own_device }))
            }
          />

          <div className="flex flex-wrap items-center justify-end gap-3">
            {total > 0 && (
              <p className="mr-auto text-sm">
                {t("numberRequests.details.hardwareTotal", { total })}
              </p>
            )}
            <Button type="submit" size="sm" disabled={isSaving}>
              {isSaving
                ? t("numberRequests.details.saving")
                : t("numberRequests.details.save")}
            </Button>
          </div>
        </form>
      )}
    </EditableBlock>
  );
}

/** Once ordered: one line per model, then what it comes to. */
function Summary({ order }: { order?: NumberRequestHardwareOrder }) {
  const { t } = useTranslation("numberRequests");

  if (!order || !hasHardwareOrder(order)) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("numberRequests.details.hardwareSummaryNone")}
      </p>
    );
  }

  const total = hardwareOrderTotal(order);

  return (
    <div className="space-y-1 text-sm">
      {NUMBER_REQUEST_HANDSETS.filter(
        (handset) => (order.quantities?.[handset] || 0) > 0,
      ).map((handset) => (
        <p key={handset}>
          {t("numberRequests.details.hardwareSummary", {
            count: order.quantities?.[handset] || 0,
            label: t(`numberRequests.details.hardwareOptions.${handset}.title`),
          })}
        </p>
      ))}

      {order.own_device && (
        <p>{t("numberRequests.details.hardwareOptions.own_device.summary")}</p>
      )}

      {total > 0 && (
        <p className="font-medium">
          {t("numberRequests.details.hardwareTotal", { total })}
        </p>
      )}
    </div>
  );
}

/**
 * One model. It is a product choice, so the photo comes first — "filaire ou
 * sans fil" is a question people answer by looking, not by reading — and the
 * whole card is the "I want one" button while nothing is ordered, so the
 * common case is a single click rather than a hunt for the stepper.
 */
function HandsetCard({
  handset,
  quantity,
  onQuantityChange,
}: {
  handset: NumberRequestHandset;
  quantity: number;
  onQuantityChange: (quantity: number) => void;
}) {
  const { t } = useTranslation("numberRequests");

  const selected = quantity > 0;

  return (
    <div
      className={cn(
        "relative flex flex-col rounded-lg border transition-colors",
        selected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
      )}
    >
      <button
        type="button"
        onClick={() => onQuantityChange(selected ? 0 : 1)}
        className="flex flex-1 flex-col gap-2 p-4 text-left"
      >
        {selected && (
          <CheckCircleIcon
            className="text-primary absolute top-3 right-3 h-5 w-5"
            weight="fill"
          />
        )}

        <img
          src={NUMBER_REQUEST_HANDSET_IMAGES[handset]}
          alt=""
          loading="lazy"
          className="mx-auto h-28 w-auto object-contain"
        />

        <span className="min-w-0 pr-6">
          <span className="block text-sm font-medium">
            {t(`numberRequests.details.hardwareOptions.${handset}.title`)}
          </span>
          <span className="text-muted-foreground mt-1 block text-sm">
            {t(`numberRequests.details.hardwareOptions.${handset}.body`)}
          </span>
          <span className="mt-1 block text-sm font-medium">
            {t("numberRequests.details.hardwareUnitPrice", {
              price: NUMBER_REQUEST_HANDSET_UNIT_PRICE_EUR,
            })}
          </span>
        </span>
      </button>

      {selected && (
        <div className="flex items-center justify-between gap-2 border-t px-4 py-2">
          <span className="text-muted-foreground text-sm">
            {t("numberRequests.details.hardwareQuantity")}
          </span>
          <div className="flex items-center gap-1">
            {/* Down to zero rather than down to one: removing the last handset
                is how a model is taken back out of the order. */}
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-7 w-7"
              aria-label={t("numberRequests.details.hardwareQuantityLess")}
              onClick={() => onQuantityChange(quantity - 1)}
            >
              <MinusIcon className="h-3 w-3" />
            </Button>
            <span className="w-6 text-center text-sm tabular-nums">
              {quantity}
            </span>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={quantity >= NUMBER_REQUEST_HANDSET_MAX_QUANTITY}
              aria-label={t("numberRequests.details.hardwareQuantityMore")}
              onClick={() =>
                onQuantityChange(
                  Math.min(NUMBER_REQUEST_HANDSET_MAX_QUANTITY, quantity + 1),
                )
              }
            >
              <PlusIcon className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "I'll bring my own phone", folded away.
 *
 * Opens on its own when it is already ticked: a saved answer must never sit
 * hidden behind a link the client has to think to click.
 */
function OwnDeviceDisclosure({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTranslation("numberRequests");

  const [open, setOpen] = useState(checked);

  useEffect(() => {
    if (checked) setOpen(true);
  }, [checked]);

  const Caret = open ? CaretDownIcon : CaretRightIcon;

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
      >
        <Caret className="h-3 w-3" weight="bold" />
        {t("numberRequests.details.hardwareOptions.own_device.title")}
      </button>

      {open && (
        <div className="flex items-start gap-2 rounded-lg border p-3">
          <Checkbox
            id="hardware-own-device"
            checked={checked}
            onCheckedChange={(value) => onChange(value === true)}
            className="mt-0.5"
          />
          <Label
            htmlFor="hardware-own-device"
            className="text-sm leading-snug font-normal"
          >
            {t("numberRequests.details.hardwareOptions.own_device.body")}
          </Label>
        </div>
      )}
    </div>
  );
}
